/**
 * Compares two values for standard Array.sort
 * @param {*} a - The first value to be compared.
 * @param {*} b - The second value to be compared.
 * @return {number}
 */
export const standardCompareValues = (a, b) => (a > b ? 1 : b > a ? -1 : 0);

/**
 * Factory that return a standard comparer by the specified key, or 'undefined' when key is falsy.
 * @param {string} key - The key by which the objects will be compared.
 * @returns {(function| undefined)}
 */
export const createSorter_compareByKey = key => {
    const undefined = void 0;
    return key
        ? (a, b) => standardCompareValues(a && a[key], b && b[key])
        : undefined; // 𝘢𝘷𝘰𝘪𝘥 𝘛𝘺𝘱𝘦𝘌𝘳𝘳𝘰𝘳 𝘰𝘯 𝘈𝘳𝘳𝘢𝘺.𝘴𝘰𝘳𝘵
};

export const byOrderKeyValue_sorter = createSorter_compareByKey('order');

/**
 * Sorts an array of objects by a specific key in alphabetical order for string values.
 * @param {Array<Object>} arrayToSort - The array of objects to be sorted.
 * @param {string} keyToMatch - The property key to sort by.
 * @returns {Array<Object>}
 */
export const alphabeticallySortArrayOfObjectsByKey = (
    arrayToSort,
    keyToMatch
) => {
    return keyToMatch
        ? arrayToSort.sort((first, second) => {
              const a = first?.[keyToMatch];
              const b = second?.[keyToMatch];
              return a && a.localeCompare && 'string' === typeof b
                  ? a.localeCompare(b)
                  : standardCompareValues(a, b);
          })
        : arrayToSort;
};

/**
 * Sorts in place, by the order of a similar array, matching items by a specific key.
 * @param {Array<Object>} arrayToSort - The array to sort
 * @param {Array<Object>} reference - The array providing the sorting order
 * @param {string} [keyToMatch = 'id'] - The object key amongst two arrays to be matched
 * @returns {Array}
 */
export const sortArrayOfObjectsByAnotherArray = (
    arrayToSort,
    reference,
    keyToMatch
) => {
    const key = keyToMatch || 'id';
    const { hasOwn } = Object;
    const getIndex = item => {
        const val = item && item[key];
        return reference.findIndex(e => e && val === e[key] && hasOwn(e, key));
    };
    return arrayToSort.sort((a, b) => getIndex(a) - getIndex(b));
};

/**
 * If an object from 'original' and an object from 'update' have the same value for the specified key,
 * then the properties from the second object will be assigned to the first: result a new array
 * where each object from the 'original' array is either left unchanged, or updated.
 * @param {Array<Object>} original - The primary array that needs to be updated
 * @param {Array<Object>} update - The array which contains the objects to update
 * @param {string} [keyToMatch='id'] - The key on which array items should be updated.
 * @returns {Array<Object>}
 */
export const updateArrayOfObjectsByKey = (original, update, keyToMatch) => {
    const key = keyToMatch || 'id';
    const { assign, hasOwn } = Object;
    return original.map(item => {
        const val = item[key];
        const found = update.find(e => e && val === e[key] && hasOwn(e, key));
        return found ? assign(item, found) : item;
    });
};

/**
 * Filters out objects from the primary array that have matching key-values in the secondary array
 * Returns an array of objects which have unique key-values in `a` corresponding to `keyToMatch`.
 * @param {Array<Object>} arrayToFilter - The primary array to filter
 * @param {Array<Object>} reference - The secondary array to compare against
 * @param {string} [keyToMatch='id'] - The object key to match between the two arrays items.
 * @returns {Array<Object>}
 */
export const filterOutIntersectingObjectsByKey = (
    arrayToFilter,
    reference,
    keyToMatch
) => {
    const arr = [];
    if (arrayToFilter?.length && reference?.length) {
        const { hasOwn } = Object;
        const key = keyToMatch || 'id';
        loop: for (const a of arrayToFilter) {
            if (a && hasOwn(a, key)) {
                const val = a[key];
                for (const b of reference) {
                    if (b && val === b[key] && hasOwn(b, key)) {
                        continue loop;
                    }
                }
            }
            arr.push(a);
        }
    }
    return arr;
};

/**
 * Reduces an array of unique objects based on the provided key.
 * @param {Array<Object>} arrayToReduce - The array of objects to reduced.
 * @param {string} [keyToMatch='id'] - The object key to test for uniqueness.
 * @returns {Array<Object>}
 */
export const reduceToUniquesByKey = (arrayToReduce, keyToMatch) => {
    const reduced = [];
    if (arrayToReduce?.length) {
        const { hasOwn } = Object;
        const key = keyToMatch || 'id';
        loop: for (const a of arrayToReduce) {
            if (a && hasOwn(a, key)) {
                const val = a[key];
                for (const item of reduced) {
                    if (val === item[key]) {
                        continue loop;
                    }
                }
                reduced.push(a);
            }
        }
    }
    return reduced;
};

/**
 * Remove duplicate by key
 * @param arr
 * @param key
 * @returns {unknown[]}
 * @author FM
 */
/*
todo:
 It's similar to "reduceToUniquesByKey" but handling objects with the same key slightly different
 and raise error on null or sparse arrays: we should decide between the two
 what is the correct management (see tests) to possibly replace it
*/
export const getUniqueListByKey = (arr, key) => {
    return arr ? [...new Map(arr.map(item => [item[key], item])).values()] : [];
};

/**
 * Get field index by internalName
 * @param {object} fields
 * @param {string} internalNameValue
 * @return {object}
 * @author sst
 */
export const getFieldIndexByInternalName = (fields, internalNameValue) =>
    fields?.findIndex(
        field => field && internalNameValue === field.internalName
    );

export const getFieldByInternalName = (fields, internalNameValue) =>
    fields?.find(field => field && internalNameValue === field.internalName);

//----- UNUSED helpers: ------------------------------------------------------------------------------------------------
/*
/**
 * Flat an array of objects recursively
 * @param data {array} - Array to flat
 * @param key {string} - Property to flat
 * @returns {*}
 * /
export function flatDeepByKey(data, key) {
    return data.reduce((prev, el) => {
        prev.push(el);
        const val = el[key];
        if (val?.length) {
            prev.push(...flatDeepByKey(val, key));
        }
        return prev;
    }, []);
}

/**
 * Get differences from two array of objects
 * @param {Array} array1
 * @param {Array} array2
 * @returns {Array} The differences
 * @author sst
 * /
export function getArrayDifferences(array1, array2, key = 'id') {
    const uniqueResultOne = array1.filter(
        arr => !array2.some(e => arr[key] === e[key])
    );
    const uniqueResultTwo = array2.filter(
        arr => !array1.some(e => arr[key] === e[key])
    );
    return uniqueResultOne.concat(uniqueResultTwo);
}

export function getIndexOfArrayElementByObjectKey(arr, obj, objKey = 'id') {
    return arr.findIndex(row => row[objKey] === obj[objKey]);
}

/**
 * Compare a list of array (as parameters) and return if are equal or not
 * @param {...Array} array - An unlimited number of array to compare
 * @param {number} [ignoreFrom] - if set the array comparison will not consider items from this index until the
 * end of the array
 * /
export function areArraysEqual() {
    const lastArgumentIndex = arguments.length - 1; // note: we can use a rest param
    const lastArgument = arguments[lastArgumentIndex];
    let ignoreFrom;

    // mm: if the last argument is a number use as ignoreFrom parameter and remove from parameter list
    if (typeof lastArgument === 'number') {
        ignoreFrom = lastArgument;
        // mm: remove last argument from list
        Array.prototype.splice.call(arguments, lastArgumentIndex, 1);
    }

    return Array.prototype.filter
        .call(arguments, arg => {
            return Array.isArray(arg);
        })
        .map(arr => {
            // mm: remove array object reference
            let array = Object.assign([], arr);

            if (typeof ignoreFrom === 'number') {
                // mm: define ignoreCount according to sign: if positive is the array length if negative is the absolute value
                let ignoreCount =
                    ignoreFrom >= 0
                        ? array.length - ignoreFrom
                        : Math.abs(ignoreFrom);

                // mm: remove all ignored item of array
                array.splice(ignoreFrom, ignoreCount);
            }
            // mm: join array into a string to make easier the comparison
            return array.join();
        })
        .reduce((result, value, index, arrays) => {
            // mm: if just one value is different the aggregated result must be false
            if (value !== arrays[0]) result = false;
            return result;
        }, true);
}

/**
 * @param originalArray - array that is managed to remove duplicates
 * @param prop - array of strings or string props to check
 * /
export function removeDuplicatesFromArrayOfObjects(originalArray, prop) {
    const isPropValuesEqual = (subject, target, propNames) =>
        propNames.every(propName => subject[propName] === target[propName]);

    const getUniqueItemsByProperties = (items, propNames) => {
        const propNamesArray = Array.from(propNames);

        return items.filter(
            (item, index, array) =>
                index ===
                array.findIndex(foundItem =>
                    isPropValuesEqual(foundItem, item, propNamesArray)
                )
        );
    };

    return getUniqueItemsByProperties(originalArray, prop);
}


*/
